import _ from 'lodash';
import BigNumber from 'bignumber.js';
import IBAN from 'iban';
import {isSepaCountryIban} from '@nexios/validation';
import parseAndGetBigNumber from '../../utils/parseAndGetBigNumber';

BigNumber.config({
  FORMAT: {
    decimalSeparator: ',',
    groupSeparator: '.',
    groupSize: 3
  }
});

const createThankYouFormConfig = ({askType, config, formContext, submit, untouchAmountFields, history, theme}) => {
  const askingRecurring = askType === 'recurring';

  const getActiveAmountConfig = (ctx) => {
    if (!askingRecurring) {
      return config;
    }
    return (config.frequencies || []).find(f => f.value === ctx.frequency) || {};
  };

  const showIcon = config.amountDisplay === 'icon';
  const customAmountDisplay = config.customAmountDisplay || 'toggle';

  const buildAmountList = (amountConfig) => {
    const amounts = (amountConfig.amounts || []).map(a => ({
      label: showIcon ? a.label : a.name,
      value: {
        value: a.amount_m.toString(),
        icon: showIcon ? a.icon : null,
        ribbon: (!_.isNil(amountConfig.defaultAmount_m) && a.amount_m.toString() === amountConfig.defaultAmount_m.toString() && amountConfig.defaultRibbon)
          ? amountConfig.defaultRibbon
          : ''
      }
    }));

    if (customAmountDisplay === 'button' && amountConfig.allowCustomAmount === true) {
      amounts.push({
        label: 'Anders',
        value: {
          value: true,
          fieldName: 'isCustomAmountVisible',
          icon: null,
          ribbon: ''
        }
      });
    }

    return amounts;
  };

  const messageForAmount = (amountConfig, amountValue) => {
    const selected = (amountConfig.amounts || []).find(a => a.amount_m.toString() === amountValue);
    return selected ? (selected.message || null) : null;
  };

  const applyFrequency = (ctx, setter, frequencyValue) => {
    const freqDef = (config.frequencies || []).find(f => f.value === frequencyValue) || (config.frequencies || [])[0];
    if (!freqDef) {
      return;
    }
    ctx.setList('amounts', buildAmountList(freqDef));
    const defaultValue = _.isNil(freqDef.defaultAmount_m) ? null : freqDef.defaultAmount_m.toString();
    setter({
      amount_m: defaultValue,
      customAmount_m: null,
      isCustomAmountVisible: customAmountDisplay === 'inline' && freqDef.allowCustomAmount === true,
      allowCustomAmount: freqDef.allowCustomAmount === true,
      amountText: messageForAmount(freqDef, defaultValue)
    });
  };

  return {
    onSubmit: (data) => {
      submit(history, data);
    },
    onInit: (ctx) => {
      if (askingRecurring) {
        ctx.setList('frequencies', (config.frequencies || []).map(f => ({label: f.label, value: f.value})));
        const defaultFrequency = config.defaultFrequency || _.get(config, ['frequencies', 0, 'value']);
        ctx.setDefaultValues({frequency: defaultFrequency, paymentMethod: 'directDebit'});
        applyFrequency(ctx, ctx.setDefaultValues, defaultFrequency);
      } else {
        ctx.setList('amounts', buildAmountList(config));
        ctx.setList('paymentMethods', config.paymentMethods || []);
        ctx.setList('idealBanks', formContext.idealBanks);
        ctx.setList('creditCardBanks', formContext.creditCardBanks);
        const defaultValue = _.isNil(config.defaultAmount_m) ? null : config.defaultAmount_m.toString();
        ctx.setDefaultValues({
          frequency: 'oneOff',
          amount_m: defaultValue,
          customAmount_m: null,
          isCustomAmountVisible: customAmountDisplay === 'inline' && config.allowCustomAmount === true,
          allowCustomAmount: config.allowCustomAmount === true,
          amountText: messageForAmount(config, defaultValue),
          paymentMethod: _.get(config, ['paymentMethods', 0, 'value']) || null
        });
      }
    },
    onDataChanged: (ctx, callback) => {
      switch (ctx.field) {
        case 'frequency':
          if (askingRecurring) {
            applyFrequency(ctx, ctx.setValues, _.get(ctx, ['data', 'frequency']));
          }
          break;
        case 'amount_m':
          ctx.setValues({
            customAmount_m: null,
            amountText: messageForAmount(getActiveAmountConfig(_.get(ctx, ['data'], {})), _.get(ctx, ['data', 'amount_m']))
          });
          if (customAmountDisplay === 'button' && !_.isNil(_.get(ctx, ['data', 'amount_m']))) {
            ctx.setValues({isCustomAmountVisible: false});
          }
          break;
        case 'customAmount_m':
          ctx.setValues({
            amount_m: null,
            amountText: null
          });
          break;
        case 'isCustomAmountVisible':
          if (_.get(ctx, ['data', 'isCustomAmountVisible']) === true && customAmountDisplay === 'button') {
            untouchAmountFields();

            ctx.setValues({
              amount_m: null,
              amountText: null
            });
          }
          if (_.get(ctx, ['data', 'isCustomAmountVisible']) === false) {
            ctx.setValues({customAmount_m: null});
          }
          break;
        default:
          break;
      }

      return callback();
    },
    fields: {
      frequency: {
        isVisible: () => askingRecurring,
        rules: [
          {
            isValid: ctx => !askingRecurring || !_.isNil(ctx.frequency),
            message: theme.showRequiredText && 'Frequentie is niet ingevuld'
          }
        ]
      },
      allowCustomAmount: {
        isVisible: () => true
      },
      amountText: {
        isVisible: () => true
      },
      amount_m: {
        isVisible: ctx => customAmountDisplay === 'toggle' ? ctx.isCustomAmountVisible !== true : true,
        rules: [
          {
            isValid: ctx => {
              const amountHasValue = !_.isNil(ctx.amount_m) && ctx.amount_m !== '';
              const customAmountHasValue = !_.isNil(ctx.customAmount_m) && ctx.customAmount_m !== '';
              return amountHasValue || customAmountHasValue;
            },
            message: theme.showRequiredText && 'Bedrag is niet ingevuld'
          }
        ]
      },
      isCustomAmountVisible: {
        isVisible: () => true
      },
      setCustomAmountVisible: {
        isVisible: ctx => customAmountDisplay === 'toggle' && ctx.allowCustomAmount === true && ctx.isCustomAmountVisible !== true
      },
      setCustomAmountHidden: {
        isVisible: ctx => customAmountDisplay === 'toggle' && ctx.allowCustomAmount === true && ctx.isCustomAmountVisible === true
      },
      customAmount_m: {
        isVisible: ctx => ctx.isCustomAmountVisible === true,
        rules: [
          {
            isValid: ctx => {
              if (!ctx.isCustomAmountVisible) {
                return true;
              }

              const amountHasValue = !_.isNil(ctx.amount_m) && ctx.amount_m !== '';
              if (amountHasValue && customAmountDisplay === 'inline') {
                return true;
              }

              const amountBn = parseAndGetBigNumber(ctx.customAmount_m);
              if (_.isNil(amountBn) || amountBn.isNaN()) {
                return true;
              }

              const active = getActiveAmountConfig(ctx);
              const maxAmount = BigNumber(active.maxAmount_m || '100000');
              const minAmount = BigNumber(active.minAmount_m || '1');

              return amountBn.isGreaterThanOrEqualTo(minAmount) && amountBn.isLessThanOrEqualTo(maxAmount);
            },
            message: ctx => {
              const active = getActiveAmountConfig(ctx);
              const maxAmount = BigNumber(active.maxAmount_m || '100000');
              const minAmount = BigNumber(active.minAmount_m || '1');
              return `Vul een bedrag in tussen € ${minAmount.toFormat(2)} en € ${maxAmount.toFormat(2)}`;
            }
          },
          {
            isValid: ctx => {
              if (!ctx.isCustomAmountVisible) {
                return true;
              }

              const amountHasValue = !_.isNil(ctx.amount_m) && ctx.amount_m !== '';
              if (amountHasValue && customAmountDisplay === 'inline') {
                return true;
              }

              const amountBn = parseAndGetBigNumber(ctx.customAmount_m);
              return !_.isNil(amountBn) && !amountBn.isNaN();
            },
            message: theme.showRequiredText && 'Bedrag is niet ingevuld'
          }
        ]
      },
      paymentMethod: {
        isVisible: () => !askingRecurring,
        rules: [
          {
            isValid: ctx => askingRecurring || !_.isNil(ctx.paymentMethod),
            message: theme.showRequiredText && 'Betaalwijze is niet ingevuld'
          }
        ]
      },
      iban: {
        isVisible: ctx => askingRecurring || ctx.paymentMethod === 'directDebit',
        isRequired: ctx => askingRecurring || ctx.paymentMethod === 'directDebit',
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.iban) && ctx.iban !== '',
            message: theme.showRequiredText && 'Rekeningnummer is niet ingevuld'
          },
          {
            message: 'Vul hier een geldig IBAN nummer in.',
            isValid: ctx => {
              const value = ctx.iban;
              if (_.isNil(value)) {
                return true;
              }
              return IBAN.isValid(value);
            }
          },
          {
            message: 'IBAN moet afkomstig zijn uit een SEPA land',
            isValid: ctx => {
              const value = ctx.iban;
              if (_.isNil(value) || value === '') return true;
              return isSepaCountryIban(value);
            }
          }
        ]
      },
      bank: {
        isVisible: ctx => !askingRecurring && ctx.paymentMethod === 'ideal' && !formContext.hideBankOptions,
        isRequired: ctx => !askingRecurring && ctx.paymentMethod === 'ideal' && !formContext.hideBankOptions,
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.bank) && ctx.bank !== '',
            message: theme.showRequiredText && 'Bank is niet ingevuld'
          }
        ]
      },
      creditCardBank: {
        isVisible: ctx => !askingRecurring && ctx.paymentMethod === 'creditCard',
        isRequired: ctx => !askingRecurring && ctx.paymentMethod === 'creditCard',
        rules: [
          {
            isValid: ctx => !_.isNil(ctx.creditCardBank) && ctx.creditCardBank !== '',
            message: theme.showRequiredText && 'Creditcard is niet ingevuld'
          }
        ]
      }
    }
  };
};

export default createThankYouFormConfig;
